import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createFileRoute } from '@tanstack/react-router'
import { useListPropertiesQuery } from '@/api'
import type { Manager, PropertyFilters } from '@/types'
import { PropertiesEmptyState } from '@/components/properties-empty-state'
import { PropertiesPageCentered, PropertiesPageLoading } from '@/components/properties-page-centered'
import { PropertiesFilters } from '@/components/properties-filters'
import { PropertiesTable } from '@/components/properties-table'
import { PanelDown } from '@/components/panel-down'
import { AddPropertyDialog } from '@/components/add-property-dialog'

export const Route = createFileRoute('/_app/properties/drafts')({
	component: PropertiesDraftsPage,
})

const PAGE_SIZE = 10

const DEFAULT_FILTERS: PropertyFilters = {
	type: '',
	status: '',
	manager: '',
	platform: '',
	published: '',
	search: '',
}

function PropertiesDraftsPage() {
	const { t } = useTranslation()
	const [page, setPage] = useState(1)
	const [filters, setFilters] = useState<PropertyFilters>(DEFAULT_FILTERS)
	const [continueId, setContinueId] = useState<string | null>(null)

	const { data, isLoading } = useListPropertiesQuery({
		status: 'DRAFT',
		limit: 100,
		...(filters.search && { search: filters.search }),
	})

	const handleFilterChange = useCallback((patch: Partial<PropertyFilters>) => {
		setFilters((prev) => ({ ...prev, ...patch }))
		setPage(1)
	}, [])

	const allProperties = data?.properties ?? []

	const uniqueManagers: Manager[] = allProperties
		.map((p) => p.manager)
		.filter((m): m is Manager => !!m)
		.filter((m, i, arr) => arr.findIndex((x) => x.id === m.id) === i)

	const q = filters.search.trim().toLowerCase()
	const filtered = allProperties
		.filter((p) => !filters.type || p.type === filters.type)
		.filter((p) => {
			if (!filters.manager) return true
			if (filters.manager === 'unassigned') return !p.manager
			return p.manager?.id === filters.manager
		})
		.filter((p) => {
			if (!q) return true
			return p.city.toLowerCase().includes(q)
				|| p.address.toLowerCase().includes(q)
				|| p.id.toLowerCase().includes(q)
				|| p.manager?.name.toLowerCase().includes(q)
		})

	const properties = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

	// Rendered outside every early return below: activating the last draft
	// empties this list, and a dialog living inside the list branch would be
	// unmounted mid-flow — taking the success flash and its "go to property"
	// action with it.
	const wizard = continueId && (
		<AddPropertyDialog
			open
			propertyId={continueId}
			onOpenChange={(o) => { if (!o) setContinueId(null) }}
		/>
	)

	if (isLoading) {
		return <PropertiesPageLoading />
	}

	// With an active search the server-side query itself can come back empty;
	// fall through to the filter empty state so the search input stays mounted.
	if (!allProperties.length && !filters.search) {
		return (
			<>
				<PropertiesPageCentered>
					<PropertiesEmptyState title={t('properties.empty.draftsTitle')} description={t('properties.empty.draftsDescription')} />
				</PropertiesPageCentered>
				{wizard}
			</>
		)
	}

	if (!filtered.length) {
		return (
			<>
				<div className="flex h-full flex-col">
					<PropertiesFilters
						filters={filters}
						onFilterChange={handleFilterChange}
						managers={uniqueManagers}
						showStatus={false}
						showPlatform={false}
					/>
					<PropertiesEmptyState title={t('properties.emptyFilterTitle')} description={t('properties.emptyFilterDescription')} />
				</div>
				{wizard}
			</>
		)
	}

	return (
		<div className="flex h-full flex-col">
			<PropertiesFilters
				filters={filters}
				onFilterChange={handleFilterChange}
				managers={uniqueManagers}
				showStatus={false}
				showPlatform={false}
			/>
			<PropertiesTable
				properties={properties}
				variant="drafts"
				onContinueDraft={(p) => setContinueId(p.id)}
			/>
			{wizard}
			<PanelDown total={filtered.length} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
		</div>
	)
}
